import React, { useState, useEffect } from 'react';
import { AlertCircle, Wrench, CheckCircle, Eye, X } from 'lucide-react';
import { jsonrepair } from 'jsonrepair';

/**
 * JSON 错误提示面板组件
 * 显示友好的错误信息，提供自动修复功能
 */
function ErrorPanel({ error, originalInput, onFixed }) {
  const [repairedJson, setRepairedJson] = useState(null);
  const [repairError, setRepairError] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  // 尝试自动修复
  useEffect(() => {
    if (!originalInput || !error) {
      setRepairedJson(null);
      setRepairError(null);
      return;
    }

    try {
      const repaired = jsonrepair(originalInput);
      // 验证修复后的 JSON 是否有效
      JSON.parse(repaired);
      setRepairedJson(repaired);
      setRepairError(null);
    } catch (err) {
      setRepairedJson(null);
      setRepairError(err.message);
    }
  }, [originalInput, error]);

  // 解析错误信息，提取位置
  const parseErrorMessage = (errorMsg) => {
    // 尝试从错误信息中提取行列号
    const positionMatch = errorMsg.match(/position (\d+)/i);
    const lineMatch = errorMsg.match(/line (\d+)/i);
    const columnMatch = errorMsg.match(/column (\d+)/i);

    let position = null;
    let line = null;
    let column = null;

    if (positionMatch) {
      position = parseInt(positionMatch[1], 10);
    }
    if (lineMatch) {
      line = parseInt(lineMatch[1], 10);
    }
    if (columnMatch) {
      column = parseInt(columnMatch[1], 10);
    }

    // 如果有 position，计算行列号
    if (position !== null && !line) {
      const beforeError = originalInput.substring(0, position);
      line = beforeError.split('\n').length;
      const lastLineBreak = beforeError.lastIndexOf('\n');
      column = position - lastLineBreak;
    }

    return { position, line, column };
  };

  // 获取错误代码片段
  const getErrorSnippet = () => {
    const { line, column, position } = parseErrorMessage(error);

    if (!line && !position) {
      return null;
    }

    const lines = originalInput.split('\n');
    const errorLine = line ? line - 1 : 0;

    // 获取错误行及其上下文（前后各 1 行）
    const startLine = Math.max(0, errorLine - 1);
    const endLine = Math.min(lines.length - 1, errorLine + 1);

    const snippet = [];
    for (let i = startLine; i <= endLine; i++) {
      snippet.push({
        lineNumber: i + 1,
        content: lines[i],
        isError: i === errorLine,
        column: i === errorLine ? column : null
      });
    }

    return snippet;
  };

  // 使用修复后的 JSON
  const handleUseRepaired = () => {
    if (repairedJson && onFixed) {
      onFixed(repairedJson);
      setShowComparison(false);
    }
  };

  const errorInfo = parseErrorMessage(error);
  const snippet = getErrorSnippet();

  return (
    <div className="mt-3 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/30 rounded-lg shadow-md overflow-hidden">
      {/* 错误信息头部 */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-semibold text-red-800 dark:text-red-200 mb-1">JSON 格式错误</h4>
            <p className="text-sm text-red-700 dark:text-red-300 mb-2">{error}</p>

            {/* 位置信息 */}
            {(errorInfo.line || errorInfo.position) && (
              <div className="text-xs text-red-600 dark:text-red-400 font-mono mb-3">
                {errorInfo.line && `第 ${errorInfo.line} 行`}
                {errorInfo.column && `，第 ${errorInfo.column} 列`}
                {errorInfo.position && ` (字符位置: ${errorInfo.position})`}
              </div>
            )}

            {/* 错误代码片段 */}
            {snippet && snippet.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded p-3 mb-3 font-mono text-xs overflow-x-auto">
                {snippet.map((line) => (
                  <div
                    key={line.lineNumber}
                    className={`flex ${line.isError ? 'bg-red-100 dark:bg-red-900/50' : ''}`}
                  >
                    <span className="text-gray-500 dark:text-gray-400 mr-3 select-none min-w-[2rem] text-right">
                      {line.lineNumber}
                    </span>
                    <span className="text-gray-800 dark:text-gray-200 whitespace-pre">
                      {line.content}
                    </span>
                  </div>
                ))}
                {/* 错误位置指示器 */}
                {snippet.some(l => l.isError && l.column) && (
                  <div className="flex">
                    <span className="mr-3 min-w-[2rem]"></span>
                    <span className="text-red-600 dark:text-red-400 whitespace-pre">
                      {' '.repeat(Math.max(0, snippet.find(l => l.isError).column - 1))}↑
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* 修复状态 */}
            <div className="flex flex-wrap gap-2">
              {repairedJson ? (
                <>
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm">
                    <CheckCircle size={16} />
                    <span className="font-medium">已找到修复方案</span>
                  </div>
                  <button
                    onClick={handleUseRepaired}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 shadow-sm"
                  >
                    <Wrench size={14} />
                    使用修复后的 JSON
                  </button>
                  <button
                    onClick={() => setShowComparison(!showComparison)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 shadow-sm"
                  >
                    <Eye size={14} />
                    {showComparison ? '隐藏' : '查看'}修复内容
                  </button>
                </>
              ) : (
                <div className="text-orange-700 dark:text-orange-300 text-sm">
                  <span className="font-medium">⚠️ 无法自动修复</span>
                  <span className="ml-2">请手动检查 JSON 格式</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 修复内容对比 */}
      {showComparison && repairedJson && (
        <div className="border-t border-red-300 dark:border-red-700 bg-white dark:bg-gray-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">修复后的 JSON</h5>
            <button
              onClick={() => setShowComparison(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={16} />
            </button>
          </div>
          <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded p-3 text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto text-gray-800 dark:text-gray-200">
            {repairedJson}
          </pre>
        </div>
      )}
    </div>
  );
}

export default ErrorPanel;
